import React, { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchJobById } from '../../api/jobs';
import { fetchRegions, fetchAllDistricts } from '../../api/stores';
import api from '../../api/http';

const JobDetailPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  // 篩選器狀態
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(null);

  const {
    data: job,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => fetchJobById(jobId as string),
    enabled: Boolean(jobId),
  });

  // 獲取區域列表
  const { data: regions = [] } = useQuery({
    queryKey: ['regions'],
    queryFn: () => fetchRegions(),
  });

  // 獲取所有區列表
  const { data: allDistricts = [] } = useQuery({
    queryKey: ['districts'],
    queryFn: () => fetchAllDistricts(),
  });

  // 檢查每個店鋪的狀態：completed（已提交）、in_progress（有草稿）、pending（未開始）
  const { data: storeStatus } = useQuery({
    queryKey: ['store-status', jobId],
    queryFn: async () => {
      if (!job?.stores || !jobId) return {};
      
      const statusMap: { [storeId: number]: 'completed' | 'in_progress' | 'pending' } = {};
      
      await Promise.all(
        job.stores.map(async (store) => {
          try {
            // 先檢查是否已提交
            const { data: submittedData } = await api.get('/service-records/by-job-store', {
              params: { job_id: jobId, store_id: store.id },
            });
            
            if (submittedData && submittedData.total_records > 0) {
              statusMap[store.id] = 'completed';
              return;
            }
            
            // 再檢查是否有草稿
            const { data: draftData } = await api.get('/service-drafts/by-store', {
              params: { job_id: jobId, store_id: store.id },
            });
            
            if (draftData && draftData.drafts && Object.keys(draftData.drafts).length > 0) {
              statusMap[store.id] = 'in_progress';
            } else {
              statusMap[store.id] = 'pending';
            }
          } catch (error) {
            statusMap[store.id] = 'pending';
          }
        })
      );
      
      return statusMap;
    },
    enabled: Boolean(job?.stores && jobId),
  });

  // 是否已選擇篩選條件
  const hasFilter = selectedRegionId !== null;

  // 根據篩選條件過濾店鋪
  const filteredStores = useMemo(() => {
    if (!job?.stores) return [];
    
    // 如果沒有選中任何篩選條件，不顯示任何店鋪
    if (!selectedRegionId) return [];
    
    return job.stores.filter((store) => {
      // 檢查區域是否匹配
      const regionMatch = store.building?.district?.region?.id === selectedRegionId;
      if (!regionMatch) return false;
      
      // 如果選中了區，檢查區是否匹配
      if (selectedDistrictId) {
        return store.building?.district?.id === selectedDistrictId;
      }
      
      return true;
    });
  }, [job?.stores, selectedRegionId, selectedDistrictId]);



  if (isLoading) {
    return <div className="panel">載入工作詳情...</div>;
  }

  if (isError || !job) {
    return (
      <div className="panel">
        <p>無法載入工作詳情。</p>
        <button className="btn-primary" onClick={() => refetch()}>
          重新載入
        </button>
        <div style={{ marginTop: '1rem' }}>
          <Link to="/jobs">回到工作列表</Link>
        </div>
      </div>
    );
  }


  return (
    <div className="panel job-detail">
      <Link to="/jobs" className="link-back">
        ← 返回工作列表
      </Link>
      <header className="job-detail-header">
        <div>
          <h2>{job.job_name}</h2>
        </div>
      </header>

      <section className="job-section">
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>涉及店鋪（{filteredStores.length} / {job.stores.length}）</h3>
          
          {/* 區域篩選器 - Icon 顯示 */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 600 }}>
              區域篩選
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {regions.map((region) => (
                <button
                  key={region.id}
                  onClick={() => {
                    if (selectedRegionId === region.id) {
                      // 如果點擊已選中的區域，取消選擇
                      setSelectedRegionId(null);
                      setSelectedDistrictId(null);
                    } else {
                      setSelectedRegionId(region.id);
                      setSelectedDistrictId(null); // 重置區選擇
                    }
                  }}
                  style={{
                    padding: '0.75rem 1.25rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '1.125rem',
                    backgroundColor: selectedRegionId === region.id ? '#2563eb' : 'white',
                    color: selectedRegionId === region.id ? 'white' : '#374151',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontWeight: selectedRegionId === region.id ? 700 : 600,
                  }}
                >
                  {region.name}
                </button>
              ))}
            </div>
          </div>

          {/* 區篩選器 - Icon 顯示（只有選擇了區域才顯示） */}
          {selectedRegionId && (
            <div>
              <div style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 600 }}>
                區篩選
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {allDistricts
                  .filter((district) => district.region_id === selectedRegionId)
                  .map((district) => (
                    <button
                      key={district.id}
                      onClick={() => {
                        if (selectedDistrictId === district.id) {
                          // 如果點擊已選中的區，取消選擇
                          setSelectedDistrictId(null);
                        } else {
                          setSelectedDistrictId(district.id);
                        }
                      }}
                    style={{
                      padding: '0.75rem 1.25rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1.125rem',
                      backgroundColor: selectedDistrictId === district.id ? '#2563eb' : 'white',
                      color: selectedDistrictId === district.id ? 'white' : '#374151',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontWeight: selectedDistrictId === district.id ? 700 : 600,
                    }}
                    >
                      {district.name}
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
        {hasFilter && (
          <p style={{ color: '#6b7280', fontSize: '1.125rem', marginBottom: '1rem', fontWeight: 500 }}>
            點擊店鋪開始服務
          </p>
        )}
        <div className="job-stores">
          {!hasFilter ? (
            // 未選擇篩選條件時的提示
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem 2rem', 
              backgroundColor: '#f0f9ff',
              borderRadius: '12px',
              border: '2px dashed #3b82f6'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
              <p style={{ 
                fontSize: '1.25rem', 
                color: '#1e40af', 
                fontWeight: 600,
                marginBottom: '0.5rem'
              }}>
                請先選擇區域
              </p>
              <p style={{ 
                fontSize: '1rem', 
                color: '#6b7280',
                marginBottom: '1rem'
              }}>
                點擊上方的區域按鈕（香港島、九龍、新界）來篩選店鋪
              </p>
              <p style={{ 
                fontSize: '0.875rem', 
                color: '#9ca3af'
              }}>
                共 {job.stores.length} 間店鋪
              </p>
            </div>
          ) : filteredStores.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              <p>沒有符合篩選條件的店鋪</p>
              <button
                onClick={() => {
                  setSelectedRegionId(null);
                  setSelectedDistrictId(null);
                }}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  color: '#2563eb',
                }}
              >
                清除篩選
              </button>
            </div>
          ) : (
            filteredStores.map((store) => (
            <div
              key={store.id}
              className="job-store-card"
              onClick={() =>
                navigate(`/jobs/${jobId}/store/${store.id}/products`)
              }
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.borderColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '';
                e.currentTarget.style.borderColor = '';
              }}
            >
              <div style={{ marginBottom: '0.75rem' }}>
                {/* 零售商名稱作為主標題 */}
                <strong style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.75rem', fontWeight: 700 }}>
                  {store.retailer?.name ?? '未有零售商資料'}
                </strong>
                
                {/* 詳細地址 */}
                <div style={{ fontSize: '1.125rem', color: '#6b7280', lineHeight: '1.6' }}>
                  {/* 區域和地區 */}
                  {store.building?.district && (
                    <p style={{ margin: '0.25rem 0', fontWeight: 500 }}>
                      {store.building.district.region?.name || ''} {store.building.district.name || ''}
                    </p>
                  )}
                  
                  {/* 建築物名稱 */}
                  {store.building?.building_name && (
                    <p style={{ margin: '0.25rem 0', fontWeight: 500 }}>
                      {store.building.building_name}
                    </p>
                  )}
                  
                  {/* 樓層和單位 */}
                  {(store.floor || store.unit) && (
                    <p style={{ margin: '0.25rem 0', fontWeight: 500 }}>
                      {store.floor && store.floor}
                      {store.floor && store.unit && ' '}
                      {store.unit && store.unit}
                    </p>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                {storeStatus?.[store.id] === 'completed' ? (
                  <>
                    <span className="status-badge status-completed" style={{ backgroundColor: '#10b981', color: 'white', fontSize: '1.125rem', fontWeight: 700, padding: '0.5rem 1rem' }}>
                      ✓ 已完成
                    </span>
                    <span style={{ color: '#10b981', fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                      <span>→</span>
                      <span>查看記錄</span>
                    </span>
                  </>
                ) : storeStatus?.[store.id] === 'in_progress' ? (
                  <>
                    <span className="status-badge" style={{ backgroundColor: '#f59e0b', color: 'white', fontSize: '1.125rem', fontWeight: 700, padding: '0.5rem 1rem', borderRadius: '6px' }}>
                      ⏳ 進行中
                    </span>
                    <span style={{ color: '#f59e0b', fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                      <span>→</span>
                      <span>繼續服務</span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="status-badge status-pending" style={{ fontSize: '1.125rem', fontWeight: 700, padding: '0.5rem 1rem' }}>
                      待處理
                    </span>
                    <span style={{ color: '#2563eb', fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                      <span>→</span>
                      <span>開始服務</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          ))
          )}
        </div>
      </section>

    </div>
  );
};

export default JobDetailPage;
